import { useEffect, useState } from 'react';

// Service Worker Registration
export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('SW registered:', registration);
          
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New version available
                  console.log('New version available!');
                  // Only show the update prompt to users on mobile devices
                  // OR users who have the PWA installed (standalone mode).
                  // Desktop users browsing the web site silently get the new bundle on next reload.
                  const isStandalone =
                    window.matchMedia('(display-mode: standalone)').matches ||
                    window.navigator.standalone === true;
                  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
                  if (isStandalone || isMobile) {
                    if (window.confirm('Nouvelle version disponible ! Voulez-vous mettre à jour ?')) {
                      window.location.reload();
                    }
                  } else {
                    // Silent update for desktop web users — next reload will pick it up.
                    console.log('[SW] Silent update applied (desktop web user).');
                  }
                }
              });
            }
          });
        })
        .catch((error) => {
          console.error('SW registration failed:', error);
        });
    });
  }
}

// Custom hook for PWA install prompt
export function usePWAInstall() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if already installed
    const checkInstalled = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      const iosStandalone = window.navigator.standalone === true;
      setIsStandalone(standalone || iosStandalone);
      setIsInstalled(standalone || iosStandalone);
    };
    
    checkInstalled();

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);
    
    // Detect mobile devices (Android, iOS, iPad)
    // Only show install prompt on mobile devices, not desktop (Windows, macOS, Linux)
    const mobileCheck = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      // Check for mobile devices
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      // Also check for tablet via screen size and touch
      const isTablet = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0)) && 
                       window.innerWidth >= 768 && window.innerWidth <= 1366;
      // Exclude desktop browsers that might report touch (like Chrome on Windows touchscreen)
      const isDesktop = /windows nt|macintosh|linux x86_64|linux i686/i.test(userAgent.toLowerCase()) && !isMobileDevice;
      
      return isMobileDevice || (isTablet && !isDesktop);
    };
    
    setIsMobile(mobileCheck());

    // Listen for beforeinstallprompt (Chrome/Android)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      // Only save the prompt if on mobile
      if (mobileCheck()) {
        setInstallPrompt(e);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for appinstalled
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const promptInstall = async () => {
    if (!installPrompt) return false;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setInstallPrompt(null);
    return outcome === 'accepted';
  };

  return {
    // Only allow install on mobile devices (Android, iOS, iPad)
    canInstall: isMobile && (!!installPrompt || (isIOS && !isStandalone)),
    isInstalled,
    isIOS,
    isStandalone,
    isMobile,
    promptInstall
  };
}

// Check if running as PWA
export function isPWA() {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true;
}

// Check network status
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
